<template>
  <Tutorial
    v-if="walkthroughController.isRunning.value"
    ref="tutorialInstance"
    :total-steps="1"
    :style="walkthroughController.tutorialStyle.value"
    :arrow-direction="walkthroughController.arrowDirection.value"
    :arrow-offset="walkthroughController.arrowOffset.value"
    :image="activeStep.image"
  >
    <template #tutorial-header>
      {{ activeStep.label }}
    </template>
    <!--
      data-testid lives on this div, not the Tutorial tag above: Tutorial.vue's
      root is a Teleport, and non-prop attrs don't fall through a Teleport
      boundary (the same reason `style` had to become a real prop) — an attr
      on <Tutorial> itself is silently dropped.
    -->
    <div data-testid="walkthrough-controller">
      <div
        v-if="activeStep.description"
        class="tw-m-0"
        v-html="activeStep.description"
      />
    </div>

    <template #tutorial-footer>
      <div class="tw-flex tw-w-full tw-items-center tw-justify-between tw-text-xs">
        <span>{{ walkthroughController.isTransitioning.value ? 'Loading…' : stepCounter }}</span>
        <div class="tw-flex tw-items-center tw-gap-4">
          <AcvButton
            v-if="!walkthroughController.isFirstStep.value"
            variant="link"
            data-testid="walkthrough-back-button"
            :disabled="walkthroughController.isTransitioning.value"
            @click="handleBack"
          >
            <span
              class="tw-rounded tw-p-1 tw-font-sans tw-text-sm tw-font-bold tw-text-secondary-base"
            >
              Previous
            </span>
          </AcvButton>
          <AcvButton
            variant="link"
            data-testid="walkthrough-advance-button"
            :disabled="walkthroughController.isTransitioning.value"
            @click="handleAdvance"
          >
            <span
              class="tw-rounded tw-p-1 tw-font-sans tw-text-sm tw-font-bold tw-text-secondary-base"
            >
              {{ walkthroughController.nextButtonLabel.value }}
            </span>
          </AcvButton>
        </div>
      </div>
    </template>
  </Tutorial>
</template>

<script setup>
import { AcvButton } from '@acv/shared-vue';
/**
 * The single, app-wide render site for the Guided Walkthrough: mount this
 * once (e.g. from layouts/default.vue). Everything that launches or
 * re-launches a walkthrough (a modal card's CTA, a What's New panel item)
 * only ever calls useWalkthroughController()'s start()/advance()/cancel() —
 * it never renders a Tutorial itself, since the Controller's state is a
 * singleton shared across every caller.
 *
 * There is currently no UI-driven cancel() path: Tutorial.vue no longer has
 * a close (X) icon (removed across every caller, not just this one), and
 * walkthroughs are short enough (3 steps or fewer) that this isn't a real
 * gap for now. cancel() itself stays implemented and tested in
 * useWalkthroughController.js for whenever a cancel affordance is added.
 */
const walkthroughController = useWalkthroughController();

/**
 * Hands the rendered tooltip to the controller so it can be measured. Positioning needs its
 * size: the tooltip is clamped inside the viewport, and you cannot clamp a box whose extent
 * you don't know. A component `ref` is used rather than an attr because Tutorial's root is a
 * Teleport, which attrs don't cross.
 */
const tutorialInstance = ref(null);

watch(tutorialInstance, (instance) => {
  walkthroughController.setTutorialElement(instance?.rootRef ?? null);
});

const activeStep = computed(() => walkthroughController.activeStep.value);

const stepCounter = computed(
  () =>
    `${walkthroughController.activeIndex.value + 1} of ${walkthroughController.totalSteps.value}`,
);

/**
 * A step transition can now navigate/run a setup (see ADR 0006), so it's no longer instant —
 * `:disabled` alone isn't a hard guard against a second click landing before Vue re-renders,
 * so both handlers also check `isTransitioning` directly before calling in.
 */
const handleAdvance = () => {
  if (walkthroughController.isTransitioning.value) return;
  walkthroughController.advance();
};

const handleBack = () => {
  if (walkthroughController.isTransitioning.value) return;
  walkthroughController.back();
};
</script>
